import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { CardModule } from 'primeng/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SummaryParent } from '@app/wrappers/summary-parent';
import { HttpClient } from '@angular/common/http';
import { environment } from '@root/src/environments/environment';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import {ThemeModule} from '@app/themes/theme-module';
import {IMenuItem} from '@models/i-menu-item';
import {MenuChildrenPipe} from '@app/utils/pipes/menu-children.pipe';

interface WeatherSummaryResponse {
  totalLayers: number;
  weatherLayers: number;
  normalizedLayers: number;
  deviationLayers: number;
  eraLayers: number;
  ibmLayers: number;
  ugmLayers: number;
  totalJobs: number;
  completedJobs: number;
  failedJobs: number;
  lastUpdateUtc: string;
  lastJobExecutionUtc: string;
}

@Component({
  selector: 'app-weather-administration-menu',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    CardModule,
    TranslateModule,
    ThemeModule,
    ProgressSpinnerModule,
    MessageModule,
    ButtonModule,
    TooltipModule,
    MenuChildrenPipe,
  ],
  templateUrl: './weather-administration-menu.component.html',
  styleUrl: './weather-administration-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeatherAdministrationMenuComponent extends SummaryParent implements OnInit, OnDestroy {
  items: IMenuItem[] = [];
  summaryData: WeatherSummaryResponse | null = null;
  loading = true;
  error: string | null = null;
  showMenu = true;
  private routerSubscription: Subscription = new Subscription();

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private detector: ChangeDetectorRef,
    private router: Router,
    private location: Location,
  ) {
    super();
  }

  ngOnInit(): void {
    this.items = [
      {
        icon: 'assets/icons/main/light/task-ead-icon.svg',
        iconBack: 'assets/icons/main/light/task-icon-line-chart-fill.svg',
        title: 'Task Scheduler',
        url: '/weather/administration/scheduler',
        cardStyle: { background: '#6DDE89' },
      },
      {
        icon: 'assets/icons/main/light/ico-document.svg',
        iconBack: 'assets/icons/main/light/normative-back-icon.svg',
        title: 'ProcessedDataProtocol',
        url: '/weather/administration/data-processing',
        cardStyle: { background: '#B48EFFCC' },
      },
      {
        icon: 'assets/icons/main/light/licon-ead-icon.svg',
        iconBack: 'assets/icons/main/light/user-icon-line-chart-fill.svg',
        title: 'Logging',
        url: '/weather/administration/logging',
        cardStyle: { background: '#B48EFFCC' },
      },
    ];
    this.loadSummaryData();
    this.updateMenuState();
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateMenuState();
      });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  goBack(): void {
    this.location.back();
  }

  private updateMenuState(): void {
    const url = this.router.url;
    this.showMenu = url === '/weather/administration' || url === '/weather/administration/';
    this.detector.detectChanges();
  }

  loadSummaryData(): void {
    this.loading = true;
    this.error = null;
    this.detector.detectChanges();

    const url = `${environment.api}/tiles/TileLayer/GetWeatherTileLayersSummary`;
    this.http.get<WeatherSummaryResponse>(url).subscribe({
      next: (data) => {
        this.summaryData = data;
        this.loading = false;
        this.detector.detectChanges();
      },
      error: (err) => {
        this.error = this.translate.instant('Failed to load weather summary');
        this.loading = false;
        this.detector.detectChanges();
      },
    });
  }
}
